import React, { useEffect, useState } from 'react';
import { getOwners, updateOwner, createOwner, getOwnerCounterparties, generateOwnerKeypair } from '../api/owners';
import { getAccounts, createAccount, updateAccount } from '../api/accounts';
import { Owner, Account, CounterpartyWithTotal } from '../types/models';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert, Typography, Accordion, AccordionSummary, AccordionDetails, Box, Drawer, IconButton, TextField, Button, Checkbox, FormControlLabel, Switch
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Address from '../components/Address';

let cachedOwners: Owner[] = [];
let cachedFilter: boolean = false;

const OwnersPage: React.FC = () => {
  const [owners, setOwners] = useState<Owner[]>([]);
  const [accountsByOwner, setAccountsByOwner] = useState<Record<number, Account[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | false>(false);
  const [accountsLoading, setAccountsLoading] = useState<Record<number, boolean>>({});
  const [accountsError, setAccountsError] = useState<Record<number, string | null>>({});
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);
  const [keypairDialogOpen, setKeypairDialogOpen] = useState(false);
  const [generatedAddress, setGeneratedAddress] = useState('');
  const [generatedSeed, setGeneratedSeed] = useState('');
  const [generatedCSV, setGeneratedCSV] = useState<string | null>(null);
  const [generateLoading, setGenerateLoading] = useState(false);
  const [generateCountInput, setGenerateCountInput] = useState<string>('1');
  const [editName, setEditName] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newOwnerName, setNewOwnerName] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [addAccountAddress, setAddAccountAddress] = useState<Record<number, string>>({});
  const [addAccountLoading, setAddAccountLoading] = useState<Record<number, boolean>>({});
  const [addAccountError, setAddAccountError] = useState<Record<number, string | null>>({});
  const [expandedCounterparties, setExpandedCounterparties] = useState<number | false>(false);
  const [counterpartiesByOwner, setCounterpartiesByOwner] = useState<Record<number, CounterpartyWithTotal[]>>({});
  const [counterpartiesLoadingByOwner, setCounterpartiesLoadingByOwner] = useState<Record<number, boolean>>({});
  const [counterpartiesErrorByOwner, setCounterpartiesErrorByOwner] = useState<Record<number, string | null>>({});
  const [showOnlyRelevantOwners, setShowOnlyRelevantOwners] = useState(false);
  const [nameFilter, setNameFilter] = useState('');

  useEffect(() => {
    if (cachedOwners.length > 0 && cachedFilter === showOnlyRelevantOwners) {
      setOwners(cachedOwners);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    getOwners(showOnlyRelevantOwners ? { with_relevant_accounts: 1 } : {})
      .then((data) => {
        setOwners(data);
        cachedOwners = data;
        cachedFilter = showOnlyRelevantOwners;
      })
      .catch((err) => setError(err.message || 'Failed to load owners'))
      .finally(() => setLoading(false));
  }, [showOnlyRelevantOwners]);

  const handleAccordionChange = (ownerId: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? ownerId : false);
    if (isExpanded && !accountsByOwner[ownerId]) {
      setAccountsLoading((prev) => ({ ...prev, [ownerId]: true }));
      getAccounts()
        .then((accounts) => {
          setAccountsByOwner((prev) => ({ ...prev, [ownerId]: accounts.filter(a => a.owner_id === ownerId) }));
          setAccountsError((prev) => ({ ...prev, [ownerId]: null }));
        })
        .catch((err) => {
          setAccountsError((prev) => ({ ...prev, [ownerId]: err.message || 'Failed to load accounts' }));
        })
        .finally(() => {
          setAccountsLoading((prev) => ({ ...prev, [ownerId]: false }));
        });
    }
  };

  const handleRowClick = (owner: Owner) => {
    setSelectedOwner(owner);
    setEditName(owner.name);
    setEditError(null);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedOwner(null);
    setEditName('');
    setEditError(null);
  };

  const handleEditSave = async () => {
    if (!selectedOwner) return;
    setEditLoading(true);
    setEditError(null);
    try {
      const updated = await updateOwner(selectedOwner.id, { name: editName });
      setOwners((prev) => prev.map((o) => (o.id === updated.id ? updated : o)));
      setSelectedOwner(updated);
      setDrawerOpen(false);
    } catch (err: any) {
      setEditError(err.message || 'Failed to update owner');
    } finally {
      setEditLoading(false);
    }
  };

  const handleCreateOpen = () => {
    setCreateDialogOpen(true);
    setNewOwnerName('');
    setCreateError(null);
  };
  const handleCreateClose = () => {
    setCreateDialogOpen(false);
    setNewOwnerName('');
    setCreateError(null);
  };
  const handleCreateSubmit = async () => {
    if (!newOwnerName.trim()) return;
    setCreateLoading(true);
    setCreateError(null);
    try {
      const created = await createOwner({ name: newOwnerName });
      setOwners((prev) => [...prev, created]);
      setCreateDialogOpen(false);
      setNewOwnerName('');
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create owner');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleAddAccountChange = (ownerId: number, value: string) => {
    setAddAccountAddress((prev) => ({ ...prev, [ownerId]: value }));
  };

  const handleAddAccount = async (ownerId: number) => {
    const address = addAccountAddress[ownerId]?.trim();
    if (!address) return;
    setAddAccountLoading((prev) => ({ ...prev, [ownerId]: true }));
    setAddAccountError((prev) => ({ ...prev, [ownerId]: null }));
    try {
      const account = await createAccount({ address, owner_id: ownerId });
      setAccountsByOwner((prev) => ({
        ...prev,
        [ownerId]: prev[ownerId] ? [...prev[ownerId], account] : [account],
      }));
      setAddAccountAddress((prev) => ({ ...prev, [ownerId]: '' }));
    } catch (err: any) {
      setAddAccountError((prev) => ({ ...prev, [ownerId]: err.message || 'Failed to add account' }));
    } finally {
      setAddAccountLoading((prev) => ({ ...prev, [ownerId]: false }));
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  const filteredOwners = owners.filter(owner =>
    owner.name && owner.name.toLowerCase().includes(nameFilter.trim().toLowerCase())
  );

  return (
    <>
      <Box mb={1}>
        <Alert severity="info">
          <strong>How to sync onchain balances or payments for all <strong>relevant</strong> accounts:</strong><br /><br />
          <strong>To pull the latest onchain <u>balances</u>:</strong><br />
          <code>PI_NETWORK=mainnet bin/rails accounts:sync_balances</code><br />
          This will sync the current onchain balance for each account marked relevant in the system.<br /><br />
          <strong>To pull <u>payments/counterparties</u> from the blockchain (full scan):</strong><br />
          <code>PI_NETWORK=mainnet bin/rails accounts:sync_relevant</code><br />
          This will scan the blockchain for payments sent by each relevant account, discover counterparties, and update transaction history.<br />
        </Alert>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', m: 2, mb: 0 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Owners</Typography>
        <FormControlLabel
          control={
            <Switch
              checked={showOnlyRelevantOwners}
              onChange={() => setShowOnlyRelevantOwners((prev) => !prev)}
              color="primary"
            />
          }
          label="Show only owners with a relevant account"
          sx={{ ml: 2 }}
        />
        <TextField
          label="Filter by owner name"
          variant="outlined"
          size="small"
          value={nameFilter}
          onChange={e => setNameFilter(e.target.value)}
          sx={{ ml: 2, width: 220 }}
        />
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateOpen} sx={{ ml: 2 }}>
          New Owner
        </Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Accounts</TableCell>
              <TableCell>Edit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOwners.map((owner) => (
              <TableRow key={owner.id} hover>
                <TableCell>{owner.id}</TableCell>
                <TableCell>{owner.name}</TableCell>
                <TableCell sx={{ p: 0 }}>
                  <Accordion
                    expanded={expanded === owner.id}
                    onChange={handleAccordionChange(owner.id)}
                    sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>Show Accounts</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {accountsLoading[owner.id] ? (
                        <CircularProgress size={20} />
                      ) : accountsError[owner.id] ? (
                        <Alert severity="error">{accountsError[owner.id]}</Alert>
                      ) : (
                        <Box>
                          {accountsByOwner[owner.id] && accountsByOwner[owner.id].length > 0 ? (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Address</TableCell>
                                  <TableCell>Name</TableCell>
                                  <TableCell>Current Balance</TableCell>
                                  <TableCell>Relevant</TableCell>
                                  <TableCell>Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {accountsByOwner[owner.id].map((account) => (
                                  <TableRow key={account.address}>
                                    <TableCell><Address address={account.address} /></TableCell>
                                    <TableCell>{account.name}</TableCell>
                                    <TableCell>{account.current_balance != null ? Number(account.current_balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}</TableCell>
                                    <TableCell>
                                      <Checkbox
                                        checked={!!account.relevant}
                                        indeterminate={account.relevant == null}
                                        onChange={async (e) => {
                                          try {
                                            const newRelevant = e.target.checked;
                                            const updated = await updateAccount(account.address, { relevant: newRelevant });
                                            setAccountsByOwner((prev) => ({
                                              ...prev,
                                              [owner.id]: (prev[owner.id] || []).map((a) => a.address === updated.address ? updated : a),
                                            }));
                                          } catch (err: any) {
                                            alert(err.message || 'Failed to update relevant status');
                                          }
                                        }}
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Button
                                        size="small"
                                        color="warning"
                                        onClick={async () => {
                                          try {
                                            await updateAccount(account.address, { owner_id: null });
                                            setAccountsByOwner((prev) => ({
                                              ...prev,
                                              [owner.id]: (prev[owner.id] || []).filter((a) => a.address !== account.address),
                                            }));
                                          } catch (e: any) {
                                            alert(e.message || 'Failed to de-associate account');
                                          }
                                        }}
                                      >
                                        Remove
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          ) : (
                            <Typography variant="body2">No accounts found for this owner.</Typography>
                          )}
                          {/* Add Account Form */}
                          <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                              label="Add Account by Address"
                              size="small"
                              value={addAccountAddress[owner.id] || ''}
                              onChange={(e) => handleAddAccountChange(owner.id, e.target.value)}
                              disabled={addAccountLoading[owner.id]}
                              sx={{ flex: 1 }}
                            />
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleAddAccount(owner.id)}
                              disabled={addAccountLoading[owner.id] || !(addAccountAddress[owner.id]?.trim())}
                            >
                              {addAccountLoading[owner.id] ? <CircularProgress size={18} /> : 'Add'}
                            </Button>
                          </Box>
                          {addAccountError[owner.id] && <Alert severity="error" sx={{ mt: 1 }}>{addAccountError[owner.id]}</Alert>}
                        </Box>
                      )}
                    </AccordionDetails>
                  </Accordion>
                  <Accordion
                    expanded={expandedCounterparties === owner.id}
                    onChange={(_e, isExpanded) => {
                      setExpandedCounterparties(isExpanded ? owner.id : false);
                      if (isExpanded && !counterpartiesByOwner[owner.id]) {
                        setCounterpartiesLoadingByOwner((prev) => ({ ...prev, [owner.id]: true }));
                        getOwnerCounterparties(owner.id)
                          .then((cps) => {
                            setCounterpartiesByOwner((prev) => ({ ...prev, [owner.id]: cps }));
                            setCounterpartiesErrorByOwner((prev) => ({ ...prev, [owner.id]: null }));
                          })
                          .catch((err) => {
                            setCounterpartiesErrorByOwner((prev) => ({ ...prev, [owner.id]: err.message || 'Failed to load counterparties' }));
                          })
                          .finally(() => {
                            setCounterpartiesLoadingByOwner((prev) => ({ ...prev, [owner.id]: false }));
                          });
                      }
                    }}
                    sx={{ boxShadow: 'none', '&:before': { display: 'none' }, mt: 1 }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography>Show Counterparties</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {counterpartiesLoadingByOwner[owner.id] ? (
                        <CircularProgress size={20} />
                      ) : counterpartiesErrorByOwner[owner.id] ? (
                        <Alert severity="error">{counterpartiesErrorByOwner[owner.id]}</Alert>
                      ) : counterpartiesByOwner[owner.id] && counterpartiesByOwner[owner.id].length > 0 ? (
                        <ul style={{ paddingLeft: 16 }}>
                          {counterpartiesByOwner[owner.id].map((cp) => (
                            <li key={cp.id}>
                              {cp.name} (ID: {cp.id})
                              <ul style={{ margin: 0, paddingLeft: 20 }}>
                                <li>
                                  {Number(cp.total_paid_to_counterparty).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })} Paid to this counterparty
                                </li>
                                <li>
                                  {Number(cp.total_received_from_counterparty).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })} Received from this counterparty
                                </li>
                              </ul>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <Typography variant="body2">No counterparties found.</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => handleRowClick(owner)} size="small">
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{ sx: { width: 340, p: 2 } }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>Edit Owner</Typography>
          <TextField
            label="Name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            fullWidth
            margin="normal"
          />
          {editError && <Alert severity="error">{editError}</Alert>}
          <Box sx={{ display: 'flex', gap: 1, mt: 2, alignItems: 'center' }}>
            <Button onClick={handleDrawerClose} disabled={editLoading}>Cancel</Button>
            <Button variant="contained" onClick={handleEditSave} disabled={editLoading || !editName.trim()}>
              {editLoading ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center' }}>
            <TextField
              label="How many?"
              type="text"
              inputMode="numeric"
              size="small"
              value={generateCountInput}
              onChange={(e) => setGenerateCountInput(e.target.value)}
              onBlur={() => {
                const n = parseInt(generateCountInput, 10);
                const clamped = Math.max(1, Math.min(100, isNaN(n) ? 1 : n));
                setGenerateCountInput(String(clamped));
              }}
              sx={{ width: 130 }}
            />
            <Button
              variant="outlined"
              onClick={async () => {
                if (!selectedOwner) return;
                setGenerateLoading(true);
                try {
                  const count = Math.max(1, Math.min(100, parseInt(generateCountInput, 10) || 1));
                  const res = await generateOwnerKeypair(selectedOwner.id, count);
                  setGeneratedCSV(res.csv || '');
                  setGeneratedAddress('');
                  setGeneratedSeed('');
                  setKeypairDialogOpen(true);
                } catch (e: any) {
                  alert(e.message || 'Failed to generate keypair');
                } finally {
                  setGenerateLoading(false);
                }
              }}
              disabled={generateLoading}
            >
              {generateLoading ? <CircularProgress size={20} /> : 'Generate keypair(s)'}
            </Button>
          </Box>
        </Box>
      </Drawer>
      <Dialog open={keypairDialogOpen} onClose={() => setKeypairDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Keypair(s) Generated</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Save this information securely now. It will not be shown again.
          </Alert>
          {generatedCSV ? (
            <Box component="pre" sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, overflowX: 'auto' }}>
              {generatedCSV}
            </Box>
          ) : (
            <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', rowGap: 1, columnGap: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Address</Typography>
              <Typography sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>{generatedAddress}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Seed</Typography>
              <Typography sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>{generatedSeed}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKeypairDialogOpen(false)} variant="contained">I’ve saved this</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={createDialogOpen} onClose={handleCreateClose} maxWidth="xs" fullWidth>
        <DialogTitle>New Owner</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={newOwnerName}
            onChange={(e) => setNewOwnerName(e.target.value)}
            fullWidth
            margin="normal"
            autoFocus
          />
          {createError && <Alert severity="error">{createError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateClose} disabled={createLoading}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateSubmit}
            disabled={createLoading || !newOwnerName.trim()}
          >
            {createLoading ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default OwnersPage; 